import { MDBCard, MDBCol, MDBRow } from "mdb-react-ui-kit";
import { FC, useState } from "react";
import { QuillDeltaToHtmlConverter } from "quill-delta-to-html";
import styles from "../../styles/detail/Detail.TravelExperiencesStepCard.module.scss";
import contactsSvg from "../../utils/svg-icon/contacts";
import socialAndBrandSvg from "../../utils/svg-icon/socialAndBrand";
import userInterfaceSvg from "../../utils/svg-icon/userInterface";
import { ItinerariesReadableTO } from "../../models/detail";
import ActivityIcon from "../common/ActivityIcon";
import { useIsMD } from "../../utils/hooks/useWindowSizes";

/* eslint-disable no-underscore-dangle */
const TravelExperiencesStepCard: FC<{
    cardData: ItinerariesReadableTO;
    isPremium: boolean;
    setShow: (el: boolean) => void;
}> = ({ cardData, isPremium, setShow }) => {
    const { CalendarLine, LocationLine, ClockLine } = contactsSvg;

    const [moreInfo, setMoreInfo] = useState<boolean>(false);

    const isMD = useIsMD();

    const converter = new QuillDeltaToHtmlConverter(
        cardData.itinerary.descriptionDelta?.ops ?? []
    );

    const html = converter.convert();

    return (
        <MDBCard
            className={
                isPremium
                    ? `${styles.cardBorderPremium}`
                    : `${styles.cardBorderQuality}`
            }
        >
            <MDBRow className="g-0">
                <MDBCol lg={9} xl={8} xxl={9}>
                    <div className="px-3 pt-3 d-flex flex-column">
                        <div className="d-flex align-items-center mb-3 text-uppercase">
                            <span className={`ps-2 ${styles.cardClockIcon}`}>
                                <CalendarLine />
                            </span>
                            <span className={styles.starText}>
                                {cardData.day}&#186; day
                            </span>
                            {cardData.itinerary.distance !== "0km" && (
                                <>
                                    {" "}
                                    <span
                                        className={`ps-2 ${styles.cardClockIcon}`}
                                    >
                                        <LocationLine />
                                    </span>
                                    <span className={styles.starText}>
                                        {cardData.itinerary.distance}
                                    </span>
                                </>
                            )}
                            {cardData.itinerary.time !==
                                "00 hours 00 minutes" && (
                                <>
                                    <span
                                        className={`ps-2 d-none d-md-block ${styles.cardClockIcon}`}
                                    >
                                        <ClockLine />
                                    </span>
                                    <span
                                        className={`d-none d-md-block ${styles.starText}`}
                                    >
                                        {cardData.itinerary.time}
                                    </span>
                                </>
                            )}
                            <span
                                className={`ps-2 ${
                                    isPremium
                                        ? styles.cardDiamondIcon
                                        : styles.cardStarQIcon
                                }`}
                            >
                                {isPremium ? (
                                    <socialAndBrandSvg.SketchLine />
                                ) : (
                                    <userInterfaceSvg.StarLine />
                                )}
                            </span>
                            <span className={styles.starText}>
                                {isPremium ? "Premium" : "Quality"}
                            </span>
                        </div>
                        <h3 className={styles.cardTitle}>
                            {cardData.itinerary.title}
                        </h3>
                    </div>
                </MDBCol>
                {!isMD && (
                    <MDBCol size={8} md={4} xl={4} xxl={3} className="ps-3">
                        <MDBRow className="px-3">
                            {cardData.itinerary.activities.map(el => (
                                <MDBCol
                                    key={el._id}
                                    className="d-inline-flex justify-content-xl-end p-0 mt-3"
                                >
                                    <ActivityIcon
                                        alt={el.name}
                                        borderColor={el.color}
                                        src={el.icon}
                                        className="p-2"
                                        isSmall
                                    />
                                </MDBCol>
                            ))}
                        </MDBRow>
                    </MDBCol>
                )}
            </MDBRow>
            <MDBRow className="g-0">
                <div className="px-3 d-flex flex-column">
                    <hr className="m-2" />
                    <div className="d-flex flex-row justify-content-between align-content-center">
                        <div>
                            <span
                                className={`${styles.cardSecondaryText} me-2`}
                            >
                                {moreInfo ? "Less info " : "More info"}
                            </span>

                            {moreInfo ? (
                                <span
                                    onClick={() => setMoreInfo(!moreInfo)}
                                    className={styles.spanMoreInfo}
                                >
                                    <userInterfaceSvg.ArrowUpSimpleLine />
                                </span>
                            ) : (
                                <span
                                    onClick={() => setMoreInfo(!moreInfo)}
                                    className={styles.spanMoreInfo}
                                >
                                    <userInterfaceSvg.ArrowDownSimpleLine />
                                </span>
                            )}
                        </div>
                        {isMD && (
                            <span
                                className={`${styles.cardSecondaryText} my-auto me-2`}
                                onClick={() => setShow(true)}
                            >
                                View map
                            </span>
                        )}
                    </div>

                    <div className="mt-3">
                        {moreInfo && (
                            <>
                                {cardData.itinerary.descriptionDelta?.ops ? (
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: html,
                                        }}
                                    />
                                ) : (
                                    <p className={styles.cardSecondaryText}>
                                        {cardData.itinerary.description}
                                    </p>
                                )}
                                {isMD && (
                                    <>
                                        <MDBRow
                                            className={`px-3 ${styles.activityText}`}
                                        >
                                            ACTIVITIES
                                        </MDBRow>
                                        <MDBRow className="px-3 pb-3 d-inline-flex">
                                            {cardData.itinerary.activities.map(
                                                el => (
                                                    <MDBCol
                                                        key={el._id}
                                                        className="d-inline-flex justify-content-xl-center p-0 mt-3"
                                                    >
                                                        <ActivityIcon
                                                            alt={el.name}
                                                            borderColor={
                                                                el.color
                                                            }
                                                            src={el.icon}
                                                            className="p-2"
                                                            isSmall
                                                        />
                                                    </MDBCol>
                                                )
                                            )}
                                        </MDBRow>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </MDBRow>
        </MDBCard>
    );
};

export default TravelExperiencesStepCard;
